import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  Paper,
  LinearProgress
} from '@mui/material';
import {
  SmartToy,
  ContentPaste,
  Campaign,
  Support,
  Help,
  Analytics,
  TrendingUp,
  Facebook,
  Instagram,
  Twitter,
  LinkedIn,
  YouTube,
  CheckCircle,
  Schedule,
  AutoAwesome,
  Psychology
} from '@mui/icons-material';

const AIAgentDashboard = () => {
  const [activeRole, setActiveRole] = useState('overview');

  const roles = [
    {
      id: 'content',
      title: 'Content Creator',
      icon: <ContentPaste />,
      color: 'primary',
      description: 'Generate engaging posts, captions, and hashtags',
      capabilities: [
        'Create viral-worthy content',
        'Generate trending hashtags',
        'Write compelling captions',
        'Design content calendars',
        'Optimize for each platform'
      ],
      metrics: {
        postsCreated: 0,
        engagementRate: '0%',
        viralPosts: 0,
        hashtagReach: '0'
      }
    },
    {
      id: 'campaigns',
      title: 'Ad Campaign Manager',
      icon: <Campaign />,
      color: 'secondary',
      description: 'Create and optimize Facebook/Instagram ad campaigns',
      capabilities: [
        'Design targeted ad campaigns',
        'Optimize budgets and targeting',
        'A/B test different creatives',
        'Track ROI and performance',
        'Scale successful campaigns'
      ],
      metrics: {
        campaignsActive: 0,
        totalSpent: '$0',
        avgROI: '0%',
        conversions: 0
      }
    },
    {
      id: 'tech',
      title: 'Tech Support Specialist',
      icon: <Support />,
      color: 'warning',
      description: 'Fix platform issues and integrations',
      capabilities: [
        'Resolve connection issues',
        'Troubleshoot integrations',
        'Guide through setup processes',
        'Optimize platform performance',
        'Provide step-by-step solutions'
      ],
      metrics: {
        issuesResolved: 89,
        avgResolutionTime: '15 min',
        satisfactionRate: '98%',
        proactiveFixes: 23
      }
    },
    {
      id: 'support',
      title: 'Customer Support Manager',
      icon: <Help />,
      color: 'info',
      description: 'Handle customer inquiries and engagement',
      capabilities: [
        'Respond to customer messages',
        'Handle negative reviews professionally',
        'Create FAQ responses',
        'Manage community engagement',
        'Build customer relationships'
      ],
      metrics: {
        inquiriesHandled: 234,
        responseTime: '2.3 min',
        satisfactionScore: '4.8/5',
        retentionRate: '94%'
      }
    },
    {
      id: 'analytics',
      title: 'Analytics Specialist',
      icon: <Analytics />,
      color: 'success',
      description: 'Track performance and generate insights',
      capabilities: [
        'Track social media performance',
        'Generate detailed reports',
        'Provide actionable insights',
        'Optimize posting strategies',
        'Predict trends and opportunities'
      ],
      metrics: {
        reportsGenerated: 67,
        insightsProvided: 234,
        accuracyRate: '96%',
        trendPredictions: 89
      }
    }
  ];

  const getRoleData = (roleId) => {
    return roles.find(role => role.id === roleId);
  };

  const renderOverview = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Your AI agent is actively managing all aspects of your social media presence. 
          Click on any role to see detailed capabilities and performance metrics.
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        {roles.map((role) => (
          <Grid item xs={12} md={6} lg={4} key={role.id}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
              onClick={() => setActiveRole(role.id)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar sx={{ bgcolor: `${role.color}.main` }}>
                    {role.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{role.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {role.description}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Key Metrics:
                  </Typography>
                  <Grid container spacing={1}>
                    {Object.entries(role.metrics).map(([key, value]) => (
                      <Grid item xs={6} key={key}>
                        <Typography variant="caption" color="text.secondary">
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {value}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                <Chip 
                  label="Active" 
                  color="success" 
                  size="small" 
                  icon={<CheckCircle />}
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderRoleDetails = (roleId) => {
    const role = getRoleData(roleId);
    if (!role) return null;

    return (
      <Box>
        <Button 
          variant="outlined" 
          onClick={() => setActiveRole('overview')}
          sx={{ mb: 3 }}
        >
          ← Back to Overview
        </Button>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Avatar sx={{ bgcolor: `${role.color}.main`, width: 64, height: 64 }}>
                {role.icon}
              </Avatar>
              <Box>
                <Typography variant="h4">{role.title}</Typography>
                <Typography variant="body1" color="text.secondary">
                  {role.description}
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Capabilities
                </Typography>
                <List>
                  {role.capabilities.map((capability, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText primary={capability} />
                    </ListItem>
                  ))}
                </List>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Performance Metrics
                </Typography>
                <Paper sx={{ p: 2 }}>
                  {Object.entries(role.metrics).map(([key, value]) => (
                    <Box key={key} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {value}
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={Math.random() * 100} 
                        color={role.color}
                        sx={{ height: 4, borderRadius: 2 }}
                      />
                    </Box>
                  ))}
                </Paper>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="body2" color="text.secondary">
                {getRecentActivity(roleId)}
              </Typography>
            </Paper>
          </CardContent>
        </Card>
      </Box>
    );
  };

  const getRecentActivity = (roleId) => {
    const activities = {
      content: 'Generated 5 new post ideas, optimized hashtags for 3 posts, and created a content calendar for next week.',
      campaigns: 'Launched 2 new ad campaigns, optimized targeting for 3 existing campaigns, and achieved 15% increase in CTR.',
      tech: 'Resolved 3 connection issues, updated platform integrations, and provided setup guidance for 2 new users.',
      support: 'Responded to 12 customer inquiries, handled 3 negative reviews professionally, and improved response time by 40%.',
      analytics: 'Generated 4 performance reports, identified 8 optimization opportunities, and predicted 3 trending topics.'
    };
    return activities[roleId] || 'No recent activity to display.';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
          <SmartToy />
        </Avatar>
        <Box>
          <Typography variant="h4">
            AI Agent Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Your Virtual Social Media Manager - Multi-Role AI Assistant
          </Typography>
        </Box>
      </Box>

      {activeRole === 'overview' ? renderOverview() : renderRoleDetails(activeRole)}
    </Box>
  );
};

export default AIAgentDashboard; 